// AI助手消息组件
import { motion } from "framer-motion";
import React, { useMemo, useState, useCallback, useEffect, memo } from "react";
import type { ReactNode } from "react";
import type { Attachment, ToolInvocation } from "@/types/chat";
import { WayBillCard } from "@/components/card/WayBillCard";
import { WeatherCard } from "@/components/card/WeatherCard";
import { MapCard } from "@/components/card/MapCard";
import {
  processMessageToStandardCards,
  extractCmdData,
  parseCmdResultFromMessage,
  extractPluginData,
  extractStreamId,
  extractThinkData,
  hasThinkStart,
  hasThinkEnd,
  parseWeatherCard,
  parseMapCard,
  processWxUrlTags,
  type WxUrlData,
} from "@/api/message/common/MessageParser";
import { WxUrlButton } from "./WxUrlButton";
import { AiThinkRender, type AiSubStep } from "./AiThinkRender";
import { MessageControl } from "./MessageControl";
import { cardServices } from "@/api/message/cardservices";
import { useSessionStore } from "@/store/SessionStore";
import { useChatContext } from "@/components/chatview/ChatContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/Accordion";
import { ChevronDown, CircleAlert } from "lucide-react";
import { markdownTableComponents } from "./MarkdwonTable";
import { cn } from "@/lib/utils";

import type { Message } from "@/types/chat";

const defaultGuessQuestions = [
  "如何优化接单效率？",
  "长途运输有哪些注意事项？",
  "如何提高订单匹配成功率？",
  "新手跑货运如何入门？",
  "如何降低空驶率？",
  "货车保养有哪些要点？",
  "如何选择合适的货源平台？",
  "跑长途如何保证休息质量？",
  "货运行业有哪些政策变化？",
  "如何处理运输过程中的纠纷？",
];

const receivedReplyGuessQuestions = [
  "收到",
  "好的，请第一时间在小程序上通知我，这些货源持续多久",
  "收到，请为我持续保持关注",
  "同意匹配，请持续为我推送",
  "同意，多给我推送点",
  "收到，有新货源请及时通知我",
  "好的，请持续关注并帮我匹配合适货源",
  "同意匹配，有合适的继续推给我",
  "收到，请保持推送不中断",
];

function GuessYouWantAsk({ useReceivedStyle = false }: { useReceivedStyle?: boolean }) {
  const { append } = useChatContext();
  const randomQuestions = useMemo(() => {
    const pool = useReceivedStyle ? receivedReplyGuessQuestions : defaultGuessQuestions;
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 3);
  }, [useReceivedStyle]);

  return (
    <div className="w-full px-2 mt-3">
      <div className="text-xs text-muted-foreground mb-2 font-medium">猜你想问</div>
      <div className="flex flex-col gap-2">
        {randomQuestions.map((question, index) => (
          <button
            key={index}
            onClick={() => {
              if (append) {
                append({
                  role: "user",
                  content: question,
                });
              }
            }}
            className="text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-700 transition-colors duration-200"
          >
            {question}
          </button>
        ))}
      </div>
    </div>
  );
}

interface AssistantMessageProps {
  content: string | ReactNode;
  toolInvocations?: ToolInvocation[];
  attachments?: Attachment[];
  isStreaming?: boolean;
  streamingSpeed?: number;
  assistantName?: string;
  avatarUrl?: string;
  isThinking?: boolean;
  onQuote?: (content: string) => void;
  previousMessage?: Message;
}

export const AssistantMessage: React.FC<AssistantMessageProps> = memo(function AssistantMessage({
  content,
  isStreaming = false,
  assistantName = "匹配与决策智能体引擎系统",
  avatarUrl = "/avatar.svg",
  onQuote,
  previousMessage,
}) {
  const { sessionId } = useSessionStore();
  const { append, setMessages, setStreamingMessageId } = useChatContext();
  const [isProcessing, setIsProcessing] = useState(false);
  const [accordionValue, setAccordionValue] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingComplete, setThinkingComplete] = useState(false);

  // 解析消息内容
  const parsedData = useMemo(() => {
    if (typeof content !== "string") {
      return {
        text: "",
        cards: null,
        cmd: null,
        result: null,
        plugins: [],
        thinkContent: null,
        weatherCard: null,
        mapCard: null,
        plainText: "",
        wxUrlMap: new Map<number, WxUrlData>(),
      };
    }

    const cards = processMessageToStandardCards(content);
    const cmd = extractCmdData(content);
    const result = parseCmdResultFromMessage(content);
    const plugins = extractPluginData(content);
    const thinkContent = extractThinkData(content);
    const weatherCard = parseWeatherCard(content);
    const mapCard = parseMapCard(content);

    // 清理文本内容逻辑
    let cleaned = content
      .replace(/<dada_card>[\s\S]*?<\/dada_card>/gi, "")
      .replace(/<cmd_result>[\s\S]*?<\/cmd_result>/gi, "")
      .replace(/<plugin>[\s\S]*?<\/plugin>/gi, "")
      .replace(/<stream_end>[\s\S]*?<\/stream_end>/gi, "")
      .replace(/<weather_card>[\s\S]*?<\/weather_card>/gi, "")
      .replace(/<map_card>[\s\S]*?<\/map_card>/gi, "");

    cleaned = cleaned.replace(/<think>[\s\S]*?<\/think>/gi, "");
    const thinkStartIndex = cleaned.indexOf("<think>");
    if (thinkStartIndex !== -1) {
      cleaned = cleaned.substring(0, thinkStartIndex);
    }

    const { markdownText, plainText, wxUrlMap } = processWxUrlTags(cleaned);

    return {
      text: markdownText.trim(),
      plainText: plainText.trim(),
      cards,
      cmd,
      result,
      plugins,
      thinkContent,
      weatherCard,
      mapCard,
      wxUrlMap,
    };
  }, [content]);

  const markdownComponents = useMemo(
    () => ({
      ...markdownTableComponents,
      a: ({ href, children, ...props }: { href?: string; children?: ReactNode }) => {
        if (href?.startsWith("#wxurl-")) {
          const id = Number.parseInt(href.slice(7), 10);
          const wxUrl = parsedData.wxUrlMap.get(id);
          if (wxUrl) {
            return <WxUrlButton wxUrl={wxUrl} />;
          }
        }
        return (
          <a href={href} {...props} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        );
      },
    }),
    [parsedData.wxUrlMap],
  );

  // 生成工具调用步骤
  const toolSteps: AiSubStep[] = useMemo(() => {
    if (!parsedData.plugins) return [];

    const steps: AiSubStep[] = [];

    parsedData.plugins.forEach((plugin) => {
      if (plugin.type === "plugin_start") {
        steps.push({
          id: plugin.id.toString(),
          title: plugin.msg,
          status: "running",
        });
      } else if (plugin.type === "plugin_end") {
        // 结束信号：更新对应步骤
        const existingStep = steps.find((s) => s.id === plugin.id.toString());
        if (existingStep) {
          existingStep.status = "success";
          existingStep.title = plugin.msg;
        }
      }
    });

    return steps;
  }, [parsedData.plugins]);

  const isAwaitingResponse = useMemo(() => {
    if (!isStreaming || typeof content !== "string") return false;

    return (
      !parsedData.text &&
      !parsedData.result &&
      !parsedData.cards?.length &&
      !parsedData.weatherCard &&
      !parsedData.mapCard &&
      toolSteps.length === 0 &&
      !isThinking
    );
  }, [isStreaming, content, parsedData, toolSteps, isThinking]);

  const currentGlobalStep = useMemo(() => {
    if (toolSteps.length > 0) {
      if (toolSteps.some((step) => step.status === "running")) {
        return 1; // 工具调用中
      }
      // 工具调用完成后，检查是否在思考
      if (isThinking && !thinkingComplete) {
        return 2; // 思考中
      }
      if (isStreaming) {
        return 3; // 答案生成中
      }
      return 4; // 完成（超出最后一步）
    }
    // 如果只有思考，没有工具调用
    if (isThinking) {
      if (!thinkingComplete) {
        return 2; // 思考中
      }
      if (isStreaming) {
        return 3; // 答案生成中
      }
      return 4; // 完成
    }

    return isStreaming ? 3 : 4;
  }, [toolSteps, isStreaming, isThinking, thinkingComplete]);

  useEffect(() => {
    if (currentGlobalStep === 1) {
      setAccordionValue("item-1");
    } else if (currentGlobalStep === 2 && isThinking) {
      // 开始思考时展开
      setAccordionValue("item-1");
    }
  }, [currentGlobalStep, isThinking]);

  // 监听内容变化，检测think标签
  useEffect(() => {
    if (typeof content !== "string") return;

    // 检测是否有think开始标签
    if (hasThinkStart(content) && !isThinking) {
      setIsThinking(true);
      setThinkingComplete(false);
    }

    // 检测是否有think闭合标签
    if (hasThinkEnd(content) && isThinking) {
      setThinkingComplete(true);
    }
    if (thinkingComplete && !isStreaming) {
      setAccordionValue("");
    }
  }, [content, isThinking, thinkingComplete, isStreaming]);

  // 处理卡片按钮点击
  const handleCardClick = useCallback(async () => {
    if (
      !parsedData.cmd ||
      !sessionId ||
      isProcessing ||
      !append ||
      !setMessages
    )
      return;

    const presetMessage = `${parsedData.cmd.button}${parsedData.cmd.order_id}`;

    setIsProcessing(true);
    // 处理卡片点击后的消息，并使用cardservers再次请求
    try {
      const newAssistantId = (Date.now() + 1).toString();
      setStreamingMessageId?.(newAssistantId);

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          role: "user",
          content: presetMessage,
        },
        {
          id: newAssistantId,
          role: "assistant",
          content: "",
        },
      ]);

      let extractedStreamId: number | undefined = undefined;

      await cardServices(
        parsedData.cmd.apiname,
        parseInt(sessionId),
        parsedData.cmd.order_id,
        (chunk: string) => {
          // 提取 stream_end 数据，并作为对话属性保存
          const streamIdData = extractStreamId(chunk);
          if (streamIdData && streamIdData.id) {
            extractedStreamId = streamIdData.id;
          }

          setMessages((prev) => {
            const lastIndex = prev.length - 1;
            if (lastIndex >= 0 && prev[lastIndex].role === "assistant") {
              const updated = [...prev];
              updated[lastIndex] = {
                ...prev[lastIndex],
                content: prev[lastIndex].content + chunk,
                streamId: extractedStreamId || prev[lastIndex].streamId,
              };
              return updated;
            }
            return prev;
          });
        }
      );
    } catch (error) {
      console.error("操作失败:", error);
    } finally {
      setIsProcessing(false);
      setStreamingMessageId?.(undefined);
    }
  }, [
    parsedData.cmd,
    sessionId,
    isProcessing,
    append,
    setMessages,
    setStreamingMessageId,
  ]);

  return (
    <motion.div
      className="w-full flex flex-col gap-1 px-4 pt-2 items-start"
      initial={isStreaming ? false : { y: 5, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
    >
      <div
        className="flex items-center gap-3 h-[40px]"
        aria-label={isAwaitingResponse ? "正在生成回复" : undefined}
      >
        <div className="h-full aspect-square border rounded-full overflow-hidden shrink-0">
          {isAwaitingResponse ? (
            <div className="chat-avatar-loading" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
          ) : (
            <img
              src={avatarUrl}
              alt={assistantName}
              className="w-full h-full object-cover"
            />
          )}
        </div>
        <div
          className={cn(
            "font-medium text-muted-foreground whitespace-nowrap leading-[40px]",
            isAwaitingResponse && "invisible",
          )}
          style={{
            fontSize: "var(--assistant-name-size)",
            color: "var(--assistant-name-color)",
          }}
          aria-hidden={isAwaitingResponse}
        >
          {assistantName}
        </div>
      </div>

      {/* AI 思考过程展示 */}
      {(toolSteps.length > 0 || isThinking) && (
        <div className="w-full pl-2">
          <Accordion
            type="single"
            collapsible
            value={accordionValue}
            onValueChange={setAccordionValue}
          >
            <AccordionItem value="item-1" className="border-none">
              <AccordionTrigger className="group  hover:no-underline">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="font-medium">思考过程</span>
                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <AiThinkRender
                  toolSteps={toolSteps}
                  currentGlobalStep={currentGlobalStep}
                  thinkContent={parsedData.thinkContent}
                  isThinking={isThinking}
                  thinkingComplete={thinkingComplete}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      )}

      <div className="flex flex-col items-start gap-4 w-full max-w-full">
        {isAwaitingResponse && (
          <p
            className="px-3 pt-1 text-base leading-[20px] m-0 invisible"
            aria-hidden="true"
          >
            &nbsp;
          </p>
        )}

        {parsedData.result && (
          <div className="px-6 bg-blue-50 border border-blue-200 rounded-lg text-blue-700">
            <span className="font-bold">操作结果：</span>
            <span className=" font-bold">{parsedData.result}</span>
          </div>
        )}

        {/* 核心内容区：包含文本和复制按钮 */}
        {typeof content === "string" && parsedData.text && (
          <div className="flex flex-col items-start gap-1 w-full">
            <div className="prose prose-neutral prose-p:my-0 prose-p:leading-[20px] prose-li:my-0 px-3 pt-1 w-full">
              {isStreaming ? (
                <p className="whitespace-pre-wrap text-base leading-[20px] m-0">
                  {parsedData.text}
                </p>
              ) : (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkBreaks]}
                  components={markdownComponents}
                >
                  {parsedData.text}
                </ReactMarkdown>
              )}
            </div>

            <MessageControl
              content={parsedData.plainText}
              isStreaming={isStreaming}
              onQuote={() => {
                const userContent =
                  previousMessage?.role === "user"
                    ? typeof previousMessage.content === "string"
                      ? previousMessage.content
                      : ""
                    : "";
                const aiContent = parsedData.plainText;
                const userSnippet = userContent.slice(0, 10);
                const aiSnippet = aiContent.slice(0, 10);

                const combinedQuote = `🙋‍♂️ : ${userSnippet}${
                  userContent.length > 10 ? "..." : ""
                }\n🤖 : ${aiSnippet}${aiContent.length > 10 ? "..." : ""}`;

                onQuote?.(combinedQuote);
              }}
              className="px-3"
            />
          </div>
        )}
        {/* ReactNode 内容 */}
        {typeof content !== "string" && (
          <div className="prose prose-neutral prose-p:my-0 prose-li:my-0">
            {content}
          </div>
        )}
        {/* 卡片列表 */}
        {parsedData.cards && parsedData.cards.length > 0 && (
          <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(300px,1fr))]">
            {parsedData.cards?.map((card) => {
              return (
              <WayBillCard
                key={card.id}
                waybillNumber={card.metadata.orderNumber}
                infos={card.infos || []}
                label={parsedData.cmd?.button || ""}
                onLabel={() => handleCardClick()}
              />
            )})}
          </div>
        )}

        {/* 天气卡片 */}
        {parsedData.weatherCard && (
          <div className="w-full max-w-md">
            <WeatherCard data={parsedData.weatherCard} />
          </div>
        )}

        {/* 地图卡片 */}
        {parsedData.mapCard && (
          <div className="w-full max-w-3xl">
            <MapCard data={parsedData.mapCard.data} />
          </div>
        )}

        {/* 回复完毕后提示内容由ai生成*/}
        {!isStreaming && (
          <div className="px-2 prose prose-neutral prose-p:my-0 prose-li:my-0">
            <p className="text-xs text-muted-foreground">
              <CircleAlert className="h-3.5 w-3.5 inline mr-1 text-muted-foreground" />
              内容由AI生成，仅供参考
            </p>
          </div>
        )}

        {/* 猜你想问 */}
        {!isStreaming && (
          <GuessYouWantAsk
            useReceivedStyle={
              typeof content === "string" && content.includes("收到")
            }
          />
        )}
      </div>
    </motion.div>
  );
});
